import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  StatusBar,
  Dimensions,
  ScrollView,
  Platform,
  Linking,
} from 'react-native';
import LinearGradient from '../components/LinearGradient';
import Icon from 'react-native-vector-icons/Ionicons';
import { useAuthContext } from '../context/AuthContext';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

const { width, height } = Dimensions.get('window');

const FEATURES = [
  { icon: 'shield-checkmark-outline', text: 'Verified profiles only' },
  { icon: 'people-outline',           text: 'Community matched' },
  { icon: 'lock-closed-outline',      text: 'Private & secure' },
];

const LoginScreen: React.FC = () => {
  const { login, loginWithApple, isLoading } = useAuthContext();
  const insets = useSafeAreaInsets();

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <StatusBar barStyle="light-content" translucent backgroundColor="transparent" />

      {/* Full-screen gradient background */}
      <LinearGradient
        colors={['#8B0A3D', '#C2185B', '#E91E8C']}
        locations={[0, 0.5, 1]}
        start={{ x: 0.1, y: 0 }}
        end={{ x: 0.9, y: 1 }}
        style={StyleSheet.absoluteFill}
      />

      {/* Decorative circles */}
      <View style={styles.circle1} />
      <View style={styles.circle2} />

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ flexGrow: 1 }}
        bounces={false}
        keyboardShouldPersistTaps="handled">

        {/* Top — branding */}
        <View style={styles.brandSection}>
          <View style={styles.logoWrap}>
            <Text style={styles.logoText}>Jeevan{'\n'}Sanyog</Text>
          </View>
          <Text style={styles.appName}>Jeevan Sanyog</Text>
          <Text style={styles.tagline}>जीवन संयोग</Text>
        </View>

        {/* Bottom card */}
        <View style={[styles.card, { paddingBottom: 36 + insets.bottom }]}>
          <Text style={styles.heading}>Matrimonial{'\n'}वैवाहिक </Text>
          <Text style={styles.sub}>
            Connecting hearts across communities with trust and tradition.
          </Text>

          {/* Feature pills */}
          <View style={styles.featureRow}>
            {FEATURES.map(f => (
              <View key={f.icon} style={styles.featurePill}>
                <Icon name={f.icon} size={14} color="#C2185B" />
                <Text style={styles.featureText}>{f.text}</Text>
              </View>
            ))}
          </View>

          {/* CTA Button */}
          <TouchableOpacity
            style={styles.loginBtn}
            onPress={login}
            disabled={isLoading}
            activeOpacity={0.85}>
            {isLoading ? (
              <ActivityIndicator color="#000" size="small" />
            ) : (
              <Text style={styles.loginBtnText}>Continue with Google or Email</Text>
            )}
          </TouchableOpacity>

          {(Platform.OS === 'ios' || Platform.OS === 'web') && (
            <TouchableOpacity
              style={styles.appleBtn}
              onPress={loginWithApple}
              disabled={isLoading}
              activeOpacity={0.85}>
              <Icon name="logo-apple" size={20} color="#000" />
              <Text style={styles.appleBtnText}>Sign in with Apple</Text>
            </TouchableOpacity>
          )}

          <Text style={styles.terms}>
            By continuing you agree to our{' '}
            <Text style={styles.termsLink} onPress={() => Linking.openURL('https://jeevansanyog.com/terms.html')}>Terms of Service</Text>
            {' '}and{' '}
            <Text style={styles.termsLink} onPress={() => Linking.openURL('https://jeevansanyog.com/privacy.html')}>Privacy Policy</Text>
          </Text>
        </View>

      </ScrollView>
    </SafeAreaView>
  );
};

export default LoginScreen;

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#8B0A3D' },

  // Decorative background circles
  circle1: {
    position: 'absolute', width: width * 0.8, height: width * 0.8,
    borderRadius: width * 0.4, backgroundColor: 'rgba(255,255,255,0.06)',
    top: -width * 0.2, right: -width * 0.2,
  },
  circle2: {
    position: 'absolute', width: width * 0.6, height: width * 0.6,
    borderRadius: width * 0.3, backgroundColor: 'rgba(255,255,255,0.05)',
    top: height * 0.15, left: -width * 0.25,
  },

  // Branding
  brandSection: {
    flex: 1, alignItems: 'center', justifyContent: 'center',
    paddingTop: 60,
  },
  logoWrap: {
    width: 100, height: 100, borderRadius: 50,
    backgroundColor: '#fff',
    alignItems: 'center', justifyContent: 'center',
    marginBottom: 20,
    shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 16,
    shadowOffset: { width: 0, height: 8 }, elevation: 10,
  },
  logoText: { fontSize: 16, fontWeight: '900', color: '#C2185B', textAlign: 'center', lineHeight: 22 },
  appName: {
    fontSize: 34, fontWeight: '900', color: '#fff',
    letterSpacing: 1.5, marginBottom: 6,
  },
  tagline: {
    fontSize: 18, color: 'rgba(255,255,255,0.75)',
    fontWeight: '500', letterSpacing: 2,
  },

  // Bottom white card
  card: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 36, borderTopRightRadius: 36,
    paddingHorizontal: 28, paddingTop: 32,
  },
  heading: {
    fontSize: 30, fontWeight: '900', color: '#1A0A0F',
    lineHeight: 38, marginBottom: 10,
  },
  sub: {
    fontSize: 15, color: '#8B6355', lineHeight: 22, marginBottom: 24,
  },

  // Feature pills
  featureRow: { gap: 10, marginBottom: 28 },
  featurePill: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: '#FFF0F4', borderRadius: 20,
    paddingHorizontal: 14, paddingVertical: 8, alignSelf: 'flex-start',
  },
  featureText: { fontSize: 13, fontWeight: '600', color: '#C2185B' },

  // Button
  loginBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    paddingHorizontal: 24, paddingVertical: 18,
    backgroundColor: '#fff', borderRadius: 16,
    borderWidth: 1, borderColor: 'rgba(0,0,0,0.1)',
    shadowColor: '#000', shadowOpacity: 0.08, shadowRadius: 8, shadowOffset: { width: 0, height: 2 }, elevation: 2,
  },
  loginBtnText: { color: '#000', fontSize: 17, fontWeight: '800' },

  appleBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10,
    backgroundColor: '#fff', borderRadius: 16,
    paddingHorizontal: 24, paddingVertical: 16, marginTop: 14,
    borderWidth: 1, borderColor: 'rgba(0,0,0,0.1)',
    shadowColor: '#000', shadowOpacity: 0.08, shadowRadius: 8, shadowOffset: { width: 0, height: 2 }, elevation: 2,
  },
  appleBtnText: { color: '#000', fontSize: 17, fontWeight: '700' },

  terms: {
    fontSize: 11, color: '#B0A09A', textAlign: 'center', marginTop: 20, lineHeight: 16,
  },
  termsLink: { color: '#C2185B', textDecorationLine: 'underline' },
});
